import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { ReceiveConfirmArrivalResultDto } from '@/entities/receive';
import { formatDisplayDate } from '@/shared/lib';

import { ConfirmArrivalWarnings } from './confirm-arrival-warnings';

interface ConfirmArrivalResultProps {
  result: ReceiveConfirmArrivalResultDto;
}

/**
 * What the receipt actually wrote. The GRN numbers matter most — the backend
 * only echoes `printGoodsReceivedNote` back (there is no print endpoint yet), so
 * showing the numbers is how a user who ticked the box gets what they asked for.
 */
export function ConfirmArrivalResult({ result }: ConfirmArrivalResultProps) {
  const { t } = useTranslation();
  const nothingWritten = result.receiveShipmentIds.length === 0;

  return (
    <div className="space-y-4">
      <Alert variant={nothingWritten ? 'info' : 'success'}>
        <CheckCircle2 />
        <AlertTitle>
          {nothingWritten
            ? t('receive.confirm.result.nothingReceived', 'No quantities were recorded')
            : t('receive.confirm.result.title', 'Receipt recorded')}
        </AlertTitle>
        <AlertDescription>
          {nothingWritten
            ? t(
                'receive.confirm.result.nothingReceivedDesc',
                'Every line was zero, so nothing was written. The shipment is still awaiting receipt.'
              )
            : t('receive.confirm.result.summary', '{{processed}} line(s) recorded at {{location}} on {{date}}', {
                processed: result.processedLines,
                location: result.receivedAtLocation,
                date: formatDisplayDate(result.receiveDate)
              })}
        </AlertDescription>
      </Alert>

      {result.goodsReceivedNoteNumbers.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{t('grn.number.label', 'Goods Received Note Number')}</p>
          <div className="flex flex-wrap gap-2">
            {result.goodsReceivedNoteNumbers.map((number) => (
              <Badge key={number} variant="outline" className="font-mono">
                {number}
              </Badge>
            ))}
          </div>
          {result.printGoodsReceivedNote && (
            <p className="text-xs text-muted-foreground">
              {t(
                'receive.confirm.result.printPending',
                'Printing the Goods Received Note is not available yet — note the number above.'
              )}
            </p>
          )}
        </div>
      )}

      {result.skippedLines > 0 && (
        <p className="text-sm text-muted-foreground">
          {t('receive.confirm.result.skipped', '{{count}} line(s) skipped because every quantity was zero.', {
            count: result.skippedLines
          })}
        </p>
      )}

      {result.consignmentStatusChanges.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t('receive.confirm.result.statusChanges', 'Consignment status changes')}
          </p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {result.consignmentStatusChanges.map((change) => (
              <li key={change.consignmentId}>
                {change.consignmentNumber}: {change.fromStatusCode} → {change.toStatusCode}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmArrivalWarnings warnings={result.warnings} />
    </div>
  );
}
